import moment from 'moment';
import { AuditActor, AuditChange, AuditEvent } from '../../components/services/auditService';
import { entityLabel, entityRank } from './auditEntities';

// Redaction markers written upstream by Defatom.Audit.Core's AuditValueFormatter.
export const MASKED_VALUE = '***';
export const HASH_PREFIX = 'sha256:';
export const TRUNCATED_SUFFIX = '...[truncated]';

export type AuditValueKind = 'empty' | 'masked' | 'hashed' | 'truncated' | 'value';

export interface AuditValueDisplay {
  kind: AuditValueKind;
  text: string;
}

/** How one old/new field value is shown. Values are already redacted by the API; this only labels them. */
export const describeAuditValue = (value: unknown): AuditValueDisplay => {
  if (value === undefined || value === null) return { kind: 'empty', text: '—' };

  if (typeof value === 'string') {
    if (value === MASKED_VALUE) return { kind: 'masked', text: 'Masked' };
    if (value.startsWith(HASH_PREFIX)) return { kind: 'hashed', text: 'Hashed (content changed)' };
    if (value.endsWith(TRUNCATED_SUFFIX)) return { kind: 'truncated', text: value.slice(0, -TRUNCATED_SUFFIX.length) };
    return { kind: 'value', text: value === '' ? '""' : value };
  }

  if (typeof value === 'number' || typeof value === 'boolean') return { kind: 'value', text: String(value) };

  // Diffs are leaf-level, so an object/array here is a single leaf such as {} or [].
  return { kind: 'value', text: JSON.stringify(value) };
};

/** Event time in the user's local time zone, in the app's usual date-time format. Zoneless values are UTC. */
export const formatAuditTime = (timestampUtc?: string | null, format = 'DD MMM YYYY, hh:mm A'): string => {
  if (!timestampUtc) return 'NA';
  const time = moment.utc(timestampUtc);
  return time.isValid() ? time.local().format(format) : 'NA';
};

export const formatAuditTimePrecise = (timestampUtc?: string | null) => formatAuditTime(timestampUtc, 'DD MMM YYYY, hh:mm:ss A (Z)');

export const actorDisplayName = (actor?: AuditActor | null): string => {
  if (!actor) return 'Unknown';
  if (actor.type === 'System') return actor.jobName ? `System: ${actor.jobName}` : 'System';
  return actor.name || actor.email || actor.userId || 'Unknown user';
};

/**
 * The acting user's own (home) tenant. Design §6's rule is homeTenantId ?? tenantId; the API computes it as
 * actorHomeTenantId, so only that field is read — never actor.homeTenantId or actor.tenantId alone.
 */
export const getActorHomeTenantId = (event: AuditEvent): string | null => event.actorHomeTenantId ?? null;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A key short enough for the list: GUIDs as their first block ("94306101…"); the full key is in the tooltip. */
const shortKey = (key: string) => (GUID.test(key) ? `${key.slice(0, 8)}…` : key);

/** One change as "Label "name"", or "Label key" when the API found no name for the record. */
export const describeChange = (change: AuditChange, keyStyle: 'short' | 'full' = 'short'): string => {
  const label = entityLabel(change.entity);
  if (change.displayName) return `${label} "${change.displayName}"`;
  if (!change.key) return label;
  return `${label} ${keyStyle === 'short' ? shortKey(change.key) : change.key}`;
};

/** The change the event is about: the first one of the main record (a plan, not its devices or status rows). */
export const primaryChange = (changes: AuditChange[]): AuditChange | undefined =>
  changes.reduce<AuditChange | undefined>((best, change) => (!best || entityRank(change.entity) < entityRank(best.entity) ? change : best), undefined);

/**
 * Short "what was acted on" text for the list, e.g. `Remediation plan "KB5126105_2026_10_12 08:25:02" (+5 more)`.
 * `countOthers: false` leaves out the "(+N more)" (tenant admins and resellers see only the main record).
 * With no changes (a denied request, a sign-in) it is the request's method and route.
 */
export const describeTarget = (event: AuditEvent, { countOthers = true }: { countOthers?: boolean } = {}): string => {
  const changes = event.changes ?? [];
  const main = primaryChange(changes);
  if (main) {
    const more = countOthers && changes.length > 1 ? ` (+${changes.length - 1} more)` : '';
    return `${describeChange(main)}${more}`;
  }
  if (event.http?.route) return `${event.http.method} ${event.http.route}`;
  return '—';
};

/** Tooltip for the Target cell: every change with its operation and full key, the main record first. */
export const describeTargetDetail = (event: AuditEvent): string => {
  const changes = event.changes ?? [];
  if (changes.length === 0) return describeTarget(event);
  const main = primaryChange(changes)!;
  return [main, ...changes.filter((change) => change !== main)]
    .map((change) => `${describeChange(change, 'full')}${change.displayName && change.key ? ` [${change.key}]` : ''} - ${change.operation}`)
    .join('\n');
};

/** List row identity: a cross-tenant search can return one document per tenant for the same event id. */
export const auditRowId = (event: AuditEvent) => `${event.eventId}:${event.tenantKey}`;
